<template>
	<TnCardHorizontal
		v-if="showCard"
		dashed
		border
		shadow="s"
		:class="{ 'padding-top-2xl': preConfigBundle?.description && bundleProduct, 'default-cursor': true }"
		ref="pre-config-bundle"
	>
		<!-- Badge -->
		<template #badge>
			<TnBadge
				v-if="showCardBadge"
				responsive
			>
				{{ cardBadgeText }}
			</TnBadge>
		</template>

		<!-- Image -->
		<template #graphic>
			<TnSkeleton
				v-if="isLoading"
				width="50"
				height="70"
				borderRadius="s"
			/>
			<NuxtImg
				v-else
				:alt="bundleProduct.title"
				:src="bundleProduct.imageLink"
				height="48px"
			/>
		</template>

		<template #default>
			<div v-if="isLoading">
				<TnSkeleton
					class="skeleton"
					v-for="width in ['50%', '60%', '25%']"
					:key="width"
					:width
					height="18"
					borderRadius="m"
				/>
			</div>
			<div v-else>
				<TnParagraph
					bold
					responsive
					size="l"
				>
					{{ cardTitle }}
				</TnParagraph>
				<TnParagraph
					responsive
					size="l"
				>
					{{ cardDescription }}
				</TnParagraph>
				<TnParagraph
					bold
					responsive
					size="l"
					class="product-price"
				>
					{{ formattedPrice }}
				</TnParagraph>
			</div>
		</template>

		<!-- Action button-->
		<template #actions>
			<TnSkeleton
				v-if="isLoading"
				width="80"
				height="30"
				borderRadius="m"
			/>
			<TnButton
				v-else
				size="xs"
				@click="addOrRemove(bundleProduct)"
				@mouseenter="setMouseHoverState(true)"
				@mouseleave="setMouseHoverState(false)"
				:iconLeft="buttonIcon"
			>
				{{ buttonLabel }}
			</TnButton>
			<TnNotification
				v-if="isError"
				type="error"
				timeout="2500"
				closeable
				position="bottom-right"
				title="Noe gikk galt"
			/>
		</template>
	</TnCardHorizontal>
</template>

<script lang="ts" setup>
import { useStore } from "vuex";
import ecommerceIntegration from "~/integrations/ecommerce-v2-integration";
import ShoppingCartItemModel from "~/helpers/ecommerce/ShoppingCartItemModel/ShoppingCartItemModel";
import { formatPrice } from "~/helpers/formatting/price";
import safeEcommerceUrl from "~/helpers/ecommerce/safeEcommerceUrl";
import { ProductCategory } from "~/components/ecommerce/types/ProductCategory";

// Props and setup
const { bundleType } = defineProps<{ bundleType: string }>();

onMounted(() => loadBundleProduct());

// Composables
const store = useStore();
const { product } = useECommerceProduct();
const { currentTerminal } = useCurrentTerminal();
const isTest = useIsTest();

// State
const bundleProduct = ref(); // The product you can add to cart
const bundlesLinkedToParent = ref<any[]>();
const isLoading = ref<boolean>(true);
const isError = ref<boolean>(false);
const isMouseHovering = ref<boolean>(false);

// Utility computed props
const visibleCartItems = computed<any[]>(() => store.getters["shoppingCart/visibleCartItems"]);
const onlyAccessoryProducts = computed<boolean>(() => store.getters["checkout/onlyAccessoryProducts"]);
const hasInventory = computed<boolean>(() => isTest || bundleProduct.value?.inventory?.[0]?.amount > 0);
const itemIdFromCart = computed<string | undefined>(() => findItemIdInCart(visibleCartItems.value));
const isAddedToCart = computed<boolean>(() => !!itemIdFromCart.value);
const preConfigBundle = computed(
	() => product.value?.preConfigBundles?.find(findBundleType) || bundlesLinkedToParent.value?.find(findBundleType),
);
const preConfigId = computed<string>(() => preConfigBundle.value?.serviceProducts?.at(0));

// Card-displayed computed properties
const showCard = computed<boolean>(() => isLoading.value || (preConfigBundle.value && hasInventory.value));
const showCardBadge = computed<boolean>(() => cardBadgeText.value && bundleProduct.value && !isLoading.value);
const cardBadgeText = computed<string>(() => preConfigBundle.value?.description);
const cardTitle = computed<string>(() => bundleType);
const cardDescription = computed<string>(() => bundleProduct.value.title);
const formattedPrice = computed<string>(() => {
	const getFormattedPrice = (price: number): string =>
		price && !isNaN(price) ? formatPrice.oneTime(price, { prefix: false, round: true }) : undefined;
	const isVatExempt = store.getters["shoppingCart/isVatExempt"];
	const prices = isVatExempt ? bundleProduct.value?.prices?.withoutVat : bundleProduct.value?.prices?.withVat;
	const price = prices?.fullPrice?.salePrice || prices?.fullPrice?.price;
	return getFormattedPrice(price);
});
const buttonLabel = computed<string>(() =>
	isAddedToCart.value ? (isMouseHovering.value ? "Fjern" : "Lagt til") : "Legg til",
);
const buttonIcon = computed<string>(() => (isAddedToCart.value ? (isMouseHovering.value ? "close" : "success") : ""));

// Methods
const setMouseHoverState = (state: boolean): void => {
	isMouseHovering.value = state;
};

const findBundleType = (p: any) => p.name === bundleType;

const loadBundleProduct = async (): Promise<void> => {
	try {
		const linkedProductIds = preConfigBundle.value?.linkedProducts || [];
		const variantParentId = product.value?.variantParentId;

		// No bundle to load
		if (linkedProductIds.length === 0 && !variantParentId) return;

		// Load linked bundle product
		if (linkedProductIds.length > 0) {
			bundleProduct.value = await ecommerceIntegration.getProductById(linkedProductIds[0]);
			return;
		}

		// Load bundle from variant parent
		bundlesLinkedToParent.value = (await ecommerceIntegration.getPreConfigBundles(
			product.value?.variantParentId,
		)) as unknown as any[]; // TODO: Add proper types to API
		bundleProduct.value = bundlesLinkedToParent.value?.find(findBundleType)?.linkedProduct;
	} catch (e) {
		isError.value = true;
		console.error(e?.response?.data || e);
	} finally {
		isLoading.value = false;
	}
};

const addOrRemove = async (item: any): Promise<void> => {
	isError.value = false;
	try {
		if (isAddedToCart.value) remove();
		else addToCart(item);
	} catch (e) {
		isError.value = true;
		console.error(e);
	}
};

// TODO: Use correct price object
const addToCart = async (item: any): Promise<void> => {
	const metadata = {
		name: item.title,
		prices: item.prices,
		price: item.priceWithVat,
		priceWithoutVat: item.price,
		salePrice: item.salePriceWithVat || undefined,
		salePriceWithoutVat: item.salePrice || undefined,
		image: item.imageLink,
		category: ProductCategory.ACCESSORY,
		preConfigId: preConfigId.value,
		badge: preConfigBundle.value.description,
		link: {
			to: safeEcommerceUrl(item.category, item.brand, item.title, item.condition),
		},
	};
	const id = onlyAccessoryProducts.value ? undefined : currentTerminal.value?.id;
	await store.dispatch(
		"shoppingCart/add",
		ShoppingCartItemModel("hardware", { id: item.id, price: item.priceWithVat, quantity: 1 }, metadata, id),
	);
};

const remove = async (): Promise<void> => {
	const items = visibleCartItems.value;
	const itemId = findItemIdInCart(items);
	await store.dispatch("shoppingCart/remove", itemId);
};

// We need to differentiate between plain bundle products and pre-config products (e.g. "ferdig påsatt").
// Otherwise, adding a plain screen protector marks the "ferdig påsatt" one as added to cart as well.
const findItemIdInCart = (items: any[]): string | undefined => {
	const targetId: string = bundleProduct.value.id;
	for (const item of items) {
		if (item.data?.id === targetId && item.metadata?.preConfigId == preConfigId.value) return item.id;
		if (item.subItems) {
			const foundId = findItemIdInCart(item.subItems);
			if (foundId) return foundId;
		}
	}
	return undefined;
};

watch(
	// Triggered on variant change (e.g. 128 gb -> 256 gb)
	() => product.value,
	(value) => {
		if (import.meta.client && value?.id && !isLoading.value) loadBundleProduct();
	},
	{ immediate: true },
);
</script>

<style lang="scss" scoped>
.default-cursor {
	cursor: default;
}

.skeleton {
	margin: $spacing-s;
}

.product-price {
	color: $color-primary-mid;
	margin-top: $spacing-xs;
}
</style>
